import React, { Component } from 'react';
import { Route, withRouter } from 'react-router-dom';
import __ from '../utils/translator';
import styled, { withTheme } from 'styled-components';
import CollectionsStore from '../flux/reports/CollectionsStore';
import PageHeader from '../components/PageHeader';
import PageHeaderMenu from '../components/PageHeaderMenu';
import ReportViewContainer from '../components/reports/ReportViewContainer';
import CollectionTypes from '../flux/reports/CollectionTypes';
import collectionTypeToParentType from '../utils/collectionTypeToParentType';
import CollectionsStoreActions from '../flux/reports/CollectionsStoreActions';
import thronThumbnail from '../utils/thronThumbnail';
import generateReportUrl from '../utils/reports/generateReportUrl';
import remToPixels from '../utils/remToPixels';
import JwtStore from '../flux/JwtStore';
import PersonalReportViewContainer
  from '../components/reports/PersonalReportViewContainer';
import thronThumbnailResize from '../utils/thronThumbnailResize';
import CollectionCardListContainer
  from "../components/CollectionCardListContainer";
import PageHeaderMenuItem from '../components/PageHeaderMenuItem';
import themeResponsiveProperty
  from "../components/responsive/themeResponsiveProperty";
import Header from '../components/organisms/Header';

const {
  COLLECTION_TYPE_SEASONAL,
  COLLECTION_TYPE_TIMELESS,
  COLLECTION_TYPE_PRODUCT_FOCUS
} = CollectionTypes;

function firstIdOfType(collectionType, collections) {
  const type = collectionTypeToParentType(collectionType);
  const firstCollection = collections.find(collection => (
    collection.type === type && (
      collectionType !== COLLECTION_TYPE_PRODUCT_FOCUS ||
      (Array.isArray(collection.productFocusGroups) && collection.productFocusGroups.length > 0)
    )
  ));

  if (firstCollection === undefined) {
    return null;
  }

  if (collectionType === COLLECTION_TYPE_PRODUCT_FOCUS) {
    return firstCollection.productFocusGroups[0].id;
  }

  return firstCollection.id;
}

class Reports extends Component {
  state = {
    collections: CollectionsStore.getState(),
  };

  subscriptions = [];

  componentDidMount() {
    CollectionsStoreActions.fetch();

    this.subscriptions.push(CollectionsStore.addListener(() => {
      this.setState({ collections: CollectionsStore.getState() });
    }));
  }

  componentWillUnmount() {
    this.subscriptions.forEach(subscription => subscription.remove());
  }


  render() {
    const firstCollectionId = firstIdOfType(COLLECTION_TYPE_SEASONAL, this.state.collections);
    const firstCategoryId = firstIdOfType(COLLECTION_TYPE_TIMELESS, this.state.collections);
    const firstProductFocusId = firstIdOfType(COLLECTION_TYPE_PRODUCT_FOCUS, this.state.collections);

    return (
      <React.Fragment>
        <Header actions={themeResponsiveProperty(['back'], undefined)(this.props)} />

        <Container>
          <PageHeader
            compact
            crumbs={[__('Home'), __('Reports')]}
            title={__('Reports')}
            cover={themeResponsiveProperty(null, thronThumbnail(this.props.theme.pageHeaderContentId.reports))(this.props)}>
            <PageHeader.MenuContainer>
              <PageHeaderMenu>
                <PageHeaderMenuLink
                  disabled={firstCollectionId === null}
                  style={{ marginLeft: 0 }}
                  isActive={this.props.match.params.collectionType === COLLECTION_TYPE_SEASONAL}
                  to={generateReportUrl(COLLECTION_TYPE_SEASONAL, { ...this.props.match.params, parentId: firstCollectionId })}>
                  {__('Seasonal')}
                </PageHeaderMenuLink>

                <PageHeaderMenuLink
                  disabled={firstCategoryId === null}
                  isActive={this.props.match.params.collectionType === COLLECTION_TYPE_TIMELESS}
                  to={generateReportUrl(COLLECTION_TYPE_TIMELESS, { ...this.props.match.params, parentId: firstCategoryId })}>
                  {__('Timeless')}
                </PageHeaderMenuLink>

                <PageHeaderMenuLink
                  disabled={firstProductFocusId === null}
                  style={{ marginRight: 0 }}
                  isActive={this.props.match.params.collectionType === COLLECTION_TYPE_PRODUCT_FOCUS}
                  to={generateReportUrl(COLLECTION_TYPE_PRODUCT_FOCUS, { ...this.props.match.params, parentId: firstProductFocusId })}>
                  {__('Product Focus')}
                </PageHeaderMenuLink>
              </PageHeaderMenu>
            </PageHeader.MenuContainer>
          </PageHeader>

          <PageHeaderSubMenu isPageLoading={this.state.isPageLoading}>
            <PageHeaderSubMenuInner>
              <Route path={`/reports/${COLLECTION_TYPE_SEASONAL}`} render={() => {
                return (
                  <CollectionCardListContainer collectionType={COLLECTION_TYPE_SEASONAL} collections={this.state.collections} match={this.props.match}/>
                )
              }} />

              <Route path={`/reports/${COLLECTION_TYPE_TIMELESS}`} render={() => {
                return (
                  <CollectionCardListContainer collectionType={COLLECTION_TYPE_TIMELESS} collections={this.state.collections} match={this.props.match}/>
                )
              }} />

              <Route path={`/reports/${COLLECTION_TYPE_PRODUCT_FOCUS}`} render={() => {
                return (
                  <CollectionCardListContainer collectionType={COLLECTION_TYPE_PRODUCT_FOCUS} collections={this.state.collections} match={this.props.match}/>
                )
              }} />
            </PageHeaderSubMenuInner>
          </PageHeaderSubMenu>

          {(JwtStore.isStoreAssistant() || JwtStore.isOffice()) ? (
            <PersonalReportViewContainer
              match={this.props.match}
              collectionType={this.props.match.params.collectionType}
              parentId={this.props.match.params.parentId} />
          ) : (
            <ReportViewContainer
              collectionType={this.props.match.params.collectionType}
              parentId={this.props.match.params.parentId} />
          )}
        </Container>
      </React.Fragment>
    );
  }
}

export default withRouter(withTheme(Reports));

const Container = styled.div`
  margin-bottom: 3rem;
`;

const PageHeaderSubMenu = styled.div``;

const PageHeaderSubMenuInner = styled.div`
  padding: ${themeResponsiveProperty('1.5rem 1rem', '1.5rem 3rem')};
  background-color: ${themeResponsiveProperty('#fff', '#000')};
  color: #fff;
  position: relative;
`;

function scaledDownCover(cover, targetWidthRem, targetHeightRem) {
  // We multiply the target size by an arbitrary number just in case we get
  // non-square images as cover (for example a 4:1 image). Since we use
  // "background-size: cover;" we need a big enough image to fill the container
  // size without getting blurred.
  return thronThumbnailResize(cover, {
    width: remToPixels(targetWidthRem * 4),
    height: remToPixels(targetHeightRem * 4),
  });
}

const PageHeaderMenuLink = ({ disabled, isActive, to, ...props }) => {
  if (disabled) {
    to = '#';
  }

  return <PageHeaderMenuItem {...props} to={to} isActive={() => isActive} />;
};
